import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/dateUtils';
import { getIndicatorStyle, type IndicatorValue } from '@/lib/settlingInIndicator';
import type {
  IndicatorDefinitionDTO,
  ProgressEntryResponse,
  ProgressIndicatorDTO,
} from '@/types/settlingIn';

interface ProgressTrendMatrixProps {
  entries: ProgressEntryResponse[];
  /**
   * The ACTIVE, ORDERED indicator set. Passed in from the caller's existing
   * `activeIndicators` memo — never recomputed here, so a deactivated or
   * reordered indicator is honoured identically by the strip and the table.
   */
  indicators: IndicatorDefinitionDTO[];
  getIndicatorLabel: (indicator: IndicatorDefinitionDTO) => string;
  className?: string;
}

/** Normalise a recorded value to the shared tri-state vocabulary. */
function toIndicatorValue(raw: string | undefined): IndicatorValue {
  return raw === 'good' || raw === 'neutral' || raw === 'bad' ? raw : 'unset';
}

/**
 * Progress trend dot-matrix (ST-6/S19).
 *
 * One row per active indicator × one column per assessment day, so "separation
 * anxiety improving, sleeping still poor" is legible without reading every row.
 *
 * Encoding: value is carried by the cell's SHAPE (border-radius + border-style)
 * from `getIndicatorStyle()`, not by hue alone, and `unset` ("not recorded")
 * stays visually distinct from `neutral` ("assessed as neutral") — different
 * domain facts. No parallel indicator vocabulary and no `settling-*` token
 * family is introduced; every colour binds to the already-gated `--capacity-*`
 * pair through the shared style lib.
 *
 * Inline divs only — a 3-level ordinal matrix needs no chart library, and this
 * is deliberately NOT a line chart.
 *
 * Consumes the progress entries the detail endpoint already returns; no new
 * fetch, no extra round-trip.
 */
export function ProgressTrendMatrix({
  entries,
  indicators,
  getIndicatorLabel,
  className,
}: ProgressTrendMatrixProps) {
  const { i18n } = useLingui();

  /**
   * Assessment days, chronologically ascending, de-duplicated by date.
   *
   * The backend enforces one progress entry per (plan, date), so a duplicate date
   * should not occur — this collapse is defensive. It keeps the FIRST entry seen
   * rather than the last so the cell agrees with the entry table, which renders
   * the same array in order and therefore also shows the first. A last-write-wins
   * `set()` silently disagreed with the table for the same date.
   */
  const columns = useMemo(() => {
    const byDate = new Map<string, ProgressEntryResponse>();
    for (const entry of entries ?? []) {
      const date = (entry.date || '').slice(0, 10);
      if (date && !byDate.has(date)) byDate.set(date, entry);
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, entry]) => ({ date, entry }));
  }, [entries]);

  const title = i18n._({ id: 'settlingIn.progress.trend.title', message: 'Trend' });

  if (indicators.length === 0 || columns.length === 0) {
    return (
      <div className={cn('rounded-lg border bg-card p-4', className)}>
        <h3 className="text-sm font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground">
          {i18n._({
            id: 'settlingIn.progress.trend.emptyState',
            message: 'No progress entries yet — the trend appears once a day is assessed.',
          })}
        </p>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={i18n._({
        id: 'settlingIn.progress.trend.ariaLabel',
        message: 'Progress trend by indicator and day',
      })}
      className={cn('rounded-lg border bg-card p-4', className)}
      data-testid="progress-trend-matrix"
    >
      <h3 className="text-sm font-semibold mb-3">{title}</h3>

      {/* Wrap-tolerant, no fixed min-widths: German indicator labels run long. */}
      <div className="space-y-2 overflow-x-auto">
        {indicators.map((indicator) => {
          const indicatorId = indicator.id || '';
          const indicatorLabel = getIndicatorLabel(indicator);

          return (
            <div
              key={indicatorId}
              className="flex flex-wrap items-center gap-2"
              data-testid={`trend-row-${indicatorId}`}
            >
              <span className="text-xs text-muted-foreground basis-full sm:basis-auto sm:min-w-0 sm:flex-shrink">
                {indicatorLabel}
              </span>
              <div className="flex flex-wrap items-center gap-1">
                {columns.map(({ date, entry }) => {
                  const recorded = (entry.indicators || []).find(
                    (i: ProgressIndicatorDTO) => i.indicator_id === indicatorId,
                  );
                  const value = toIndicatorValue(recorded?.value);
                  const style = getIndicatorStyle(value);
                  const localizedDate = formatDate(date, i18n.locale);
                  const localizedValue = i18n._(style.label);

                  const cellLabel = i18n._({
                    id: 'settlingIn.progress.trend.cellAriaLabel',
                    message: '{indicator} on {date}: {value}',
                    values: {
                      indicator: indicatorLabel,
                      date: localizedDate,
                      value: localizedValue,
                    },
                  });
                  // Tooltip carries date · value · note, the note being the
                  // entry's comment.
                  const tooltip = i18n._({
                    id: 'settlingIn.progress.trend.cellTooltip',
                    message: '{date} · {value}{note}',
                    values: {
                      date: localizedDate,
                      value: localizedValue,
                      note: entry.comment ? ` — ${entry.comment}` : '',
                    },
                  });

                  return (
                    <span
                      key={`${indicatorId}-${date}`}
                      // Value is carried by `shape` (border-radius +
                      // border-style), so it survives greyscale.
                      className={cn(
                        'h-4 w-4 shrink-0',
                        style.chip,
                        style.text,
                        style.shape,
                      )}
                      role="img"
                      aria-label={cellLabel}
                      title={tooltip}
                      data-testid={`trend-cell-${indicatorId}-${date}`}
                      data-value={value}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
